import {Box, Flex, useTheme, VStack} from '@chakra-ui/react';
import {QRCodeSVG} from 'qrcode.react';
import React, {useCallback, useEffect, useState} from 'react';
import Countdown from 'react-countdown';
import {fetchUserReferrals} from '../../../../../../endpoints/api';
import {fetchMembershipStripeLinkWithAnalytics} from '../../../../../../utils/membershipCheckout';
import {ActiveRedemption} from '../../../../../../hooks/useActiveRedemptions';
import useAuth from '../../../../../../hooks/useAuth';
import {Offer, OfferType} from '../../../../../../types/article';
import {BlueButton, WhiteButton} from '../../../../../button';
import {ToastMessage} from '../../../../../toast';
import {TextA18, TextG10, TextG11, TextG12, TextG14, TextG16} from '../../../../../typography';
import {MembershipReferral} from '../../../../../ReferralsList';

const RedemptionStepThree = ({
  offer,
  setShowModal,
  timeRemaining,
  voucherCode,
  removeActiveRedemption,
  activeRedemption,
  voucher,
  redemptionLink,
}: {
  offer: Offer;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  timeRemaining: number;
  voucherCode: string;
  removeActiveRedemption?: (redemption: any) => void;
  activeRedemption?: ActiveRedemption;
  voucher?: Record<string, string> | null;
  redemptionLink?: string;
}) => {
  const theme = useTheme();
  const pointCost = offer.points_redeem_amount;
  const {user} = useAuth();
  const [referrals, setReferrals] = useState<MembershipReferral[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch referrals data if offer is membership related
    if (offer.offer_type === OfferType.MEMBERSHIP_GIVE) {
      const loadReferrals = async () => {
        try {
          const response = await fetchUserReferrals();
          if (response?.data?.membership_referrals) {
            setReferrals(response.data.membership_referrals);
          }
        } catch (error) {
          console.error('Error fetching referrals:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadReferrals();
    }
  }, [offer.offer_type]);

  const renderer = useCallback(({hours, minutes, seconds, completed, formatted}) => {
    if (offer?.offer_type == OfferType.EVENT_REDEMPTION || !offer?.countdown_required) return null;
    return (
      <TextG12>
        {minutes}:{formatted.seconds} Remaining
      </TextG12>
    );
  }, []);

  return (
    <>
      <TextG12
        py="2px"
        color={theme.colors.lightestGrey}
        px={4}
        fontWeight="500"
        isTruncated="&hellip;"
        noOfLines={1}>
        {`${pointCost.toLocaleString()} points used for this redemption`}
      </TextG12>
      {offer.offer_type === OfferType.EVENT_REDEMPTION ? (
        <Box
          py={3}
          px={2}
          textAlign="center"
          border={`1px solid ${theme.colors.lightGrey}`}
          borderRadius="7px">
          <VStack>
            <TextG10 fontSize="20px" color="#329241" fontWeight="700">
              You're Going!
            </TextG10>
            <TextG14
              fontWeight="500"
              color={theme.colors.grey}>{`RSVP for: ${user?.name} ${user?.surname}`}</TextG14>
            <TextG14 fontWeight="400" color={theme.colors.grey}>{`1 ${
              offer.quantity_type ? ` ${offer.quantity_type}` : ``
            } confirmed`}</TextG14>
            <TextG14 fontWeight="400" color={theme.colors.grey}>
              This voucher is saved in your Perks. We'll be sending you more details by e-mail as we
              get closer to the date.
            </TextG14>
            <TextG14 fontWeight="400" color={theme.colors.grey}>
              {`CODE: ${voucherCode}`}
            </TextG14>
          </VStack>
        </Box>
      ) : offer.offer_type === OfferType.MEMBERSHIP_GET ||
        offer.offer_type === OfferType.MEMBERSHIP_GIVE ? (
        <Box
          pt={2}
          pb={4}
          px={2}
          textAlign="center"
          border={`1px dashed ${theme.colors.rose}`}
          borderRadius="12.5px">
          <VStack>
            <TextG14 fontWeight="700">FIRST MONTH FREE!</TextG14>
            <TextG12 fontWeight="700" color={theme.colors.rose}>
              LOCAL INSIDERS CLUB ACCESS
            </TextG12>
            <TextG10 fontWeight="400" color={theme.colors.grey}>
              Click the button below to copy the link. From there you can email or send it in a
              text. Note: Link is good for one use only.
            </TextG10>
          </VStack>
        </Box>
      ) : (
        <Box
          pt={2}
          pb={4}
          px={2}
          textAlign="center"
          border={`1px dashed ${theme.colors.blue}`}
          borderRadius="7px">
          <VStack>
            <TextG14 fontWeight="700">Present this code at checkout</TextG14>
            <TextA18 color="green">{voucherCode}</TextA18>
            <Countdown
              onComplete={() => {
                if (removeActiveRedemption) {
                  if (activeRedemption) {
                    removeActiveRedemption(activeRedemption);
                  } else {
                    if (voucher) {
                      // removeActiveRedemption({
                      //   id: timeRemaining,
                      //   offer: offer,
                      //   voucherCode: voucher?.code,
                      //   timeRemaining: timeRemaining,
                      // });
                    }
                  }
                }
                setShowModal(false);
              }}
              date={timeRemaining}
              renderer={renderer}
            />
          </VStack>
        </Box>
      )}

      {offer.offer_type === OfferType.MEMBERSHIP_GET ? (
        <Flex flexDir="column" gap="8px" w="100%">
          <BlueButton
            background={theme.colors.blue}
            onClick={async () => {
              fetchMembershipStripeLinkWithAnalytics({
                membershipId: Number(2),
                cLink: `${location.origin}/my-stuff`,
                promoCode: voucher?.promo_code || activeRedemption?.promoCode,
              })
                .then((ret) => {
                  if (ret.data.link) {
                    window.location.href = ret.data.link;
                  }
                })
                .catch((error) => {
                  const errString = error.response.data.error;

                  ToastMessage({
                    status: 'error',
                    text:
                      errString ||
                      'Something went wrong while generating your membership link. Please contact support.',
                  });
                });
            }}
            _focus={{boxShadow: 'none'}}
            _hover={{background: ''}}>
            <TextG12 color={theme.colors.white} fontWeight="500">
              Use for Myself
            </TextG12>
          </BlueButton>
          <WhiteButton
            onClick={() => setShowModal(false)}
            _focus={{boxShadow: 'none'}}
            _hover={{background: ''}}>
            <TextG12 textDecoration="none" fontWeight="400" color={theme.colors.grey}>
              Not Now
            </TextG12>
          </WhiteButton>
        </Flex>
      ) : offer.offer_type === OfferType.MEMBERSHIP_GIVE ? (
        <Flex flexDir="column" gap="8px" w="100%">
          {referrals.length > 0 && (
            <TextG16 textAlign="center">
              You've had <span style={{color: 'blue', fontWeight: 500}}>{referrals.length}</span>{' '}
              friends accept your invitation to Local Insiders Club and earned
              <span style={{color: 'blue', fontWeight: 500}}> {referrals.length}</span> free months!
            </TextG16>
          )}
          {redemptionLink && (
            <Flex flexDir="column" alignItems="center" gap={4}>
              <TextG14 color={theme.colors.lightBlack} fontWeight="500">
                Have Your Friend Scan This QR Code to Redeem
              </TextG14>
              <QRCodeSVG value={redemptionLink} size={120} />
              <TextG16 mb={2} color={theme.colors.lightBlack} fontWeight="500">
                OR
              </TextG16>
            </Flex>
          )}
          <BlueButton
            background={theme.colors.blue}
            onClick={() => {
              navigator.clipboard.writeText(redemptionLink || '');
              ToastMessage({
                status: 'success',
                text: 'Gift link copied. Now send it to your friend!',
              });
            }}
            _focus={{boxShadow: 'none'}}
            _hover={{background: ''}}>
            <TextG12 color={theme.colors.white} fontWeight="500">
              Copy Gift Link and Send it to a Friend
            </TextG12>
          </BlueButton>
          <WhiteButton
            onClick={() => setShowModal(false)}
            _focus={{boxShadow: 'none'}}
            _hover={{background: ''}}>
            <TextG12 textDecoration="none" fontWeight="400" color={theme.colors.grey}>
              Not Now
            </TextG12>
          </WhiteButton>
        </Flex>
      ) : (
        <Box>
          <BlueButton
            background={theme.colors.blue}
            w="180px"
            onClick={() => setShowModal(false)}
            _focus={{boxShadow: 'none'}}
            _hover={{background: ''}}>
            <TextG12 color={theme.colors.white} fontWeight="500">
              Done
            </TextG12>
          </BlueButton>
        </Box>
      )}
    </>
  );
};

export default RedemptionStepThree;
